// Types
import type { ContextualMenuPerformAction } from '@jet-app/music-classical/action-model/contextual-menu/PerformAction';
import type { Jet } from '~/jet';
import type { LoggerFactory } from '@amp/web-apps-logger';

// Utils
import { removePlaylist } from '~/stores/playlists';

// Utils for Intents
import { makeContextMenuIntent } from '@jet-app/music-classical/intents/context-menu/ContextMenuIntent';
import { updateLibraryState } from '~/stores/local-library-state';
import { makeInvalidationAction } from '@jet-app/music-classical/action-model/InvalidationAction';

export type Dependencies = {
    jet: Jet;
    logger: LoggerFactory;
};

export function registerHandler(dependencies: Dependencies) {
    const { jet, logger } = dependencies;

    const log = logger.loggerFor(
        'jet/action-handlers/contextual-menu-perform-action',
    );

    jet.onAction(
        'contextualMenuPerformAction',
        async (action: ContextualMenuPerformAction) => {
            log.info('received ContextualMenuPerformAction:', action);

            try {
                const apiAction = makeContextMenuIntent({
                    url: action.url,
                    actionType: 'perform',
                    payload: action.payload,
                });

                const response = await jet.dispatch(apiAction);

                // For Delete Playlist from Library, we receive Navigate Back Response, so we navigate back
                if (response?.type === 'navigate-back') {
                    // Remove library playlist from sidebar, if it is being deleted from Library
                    removePlaylist(action?.payload?.id);

                    window.history.back();
                    return;
                }

                // For Library Status Update, update the Local Library State
                const isLibraryOrFavorite =
                    action.url.includes('/command/library/') ||
                    action.url.includes('/command/favorites/');
                const payload = action.payload as {
                    id: string;
                    inLibrary: boolean;
                    entityType: string;
                    favorite: boolean;
                };
                if (isLibraryOrFavorite && action.payload) {
                    const updateState: any = {
                        type: payload.entityType,
                        id: payload.id,
                    };

                    if ('inLibrary' in payload) {
                        updateState.inLibrary = payload.inLibrary;
                    }

                    if ('favorite' in payload) {
                        updateState.inFavorites = payload.favorite;
                    }

                    updateLibraryState(payload?.id, updateState);
                }

                // If the payload is removing an item from library refresh Library Playlists page, or explicit action of Removing a Track from Playlist
                // Both are valid option to delete
                const isDeleteTrackOperation =
                    action?.payload?.['removeItem'] === true ||
                    action?.url?.includes('path=remove-track-from-playlist');
                if (
                    isDeleteTrackOperation ||
                    (isLibraryOrFavorite &&
                        payload?.entityType === 'library-playlist')
                ) {
                    jet.perform(
                        makeInvalidationAction({
                            kind: 'libraryPlaylistUpdated',
                        }),
                    );
                }

                return 'performed';
            } catch (e) {
                log.error('Contextual Menu Perform Action Error:', e);
                throw new Error(
                    'an error occurred while handling Contextual Menu PerformAction',
                );
            }
        },
    );
}
